import type {
  TrainingBudgetUnit,
  TrainingSessionDetailView,
  TrainingSessionMetricsView,
} from "@/api";
import { assignChartSeriesSlots, CHART_SERIES_PALETTE } from "@/shared/ui/chart/public";
import {
  metricAxisUnit,
  resolveSessionBudgetUnit,
} from "../sessionDetail/analysis/sessionAnalysisModel";

// 비교 화면의 순수 파생 로직. 시리즈 색, 지표 키 합집합, X축 단위 혼합 판정을 컴포넌트 밖에서
// 소유하고 단위 테스트로 고정한다.

// 비교 화면 기본 지표. 세션 상세의 defaultMetricKeys 와 같은 값이다.
export const DEFAULT_COMPARE_METRIC_KEYS: readonly string[] = ["training_loss", "validation_loss"];

// 선택 상한(MAX_SESSION_COMPARE_SELECTION)이 팔레트보다 작으므로 색이 반복되지 않는다
// (modulo 는 안전장치다).
export function sessionSeriesColor(slot: number): string {
  return CHART_SERIES_PALETTE[slot % CHART_SERIES_PALETTE.length];
}

// 선택을 바꿔 다시 열어도 남은 세션이 이전 색을 유지하도록 직전 슬롯 배정을 기억한다.
// 비교 버튼(이벤트 핸들러)에서만 호출하므로 렌더 멱등성은 필요 없다.
let prevCompareSlots: Record<string, number> = {};

export function assignSessionCompareSlots(ids: readonly string[]): Record<string, number> {
  const slots = assignChartSeriesSlots(ids, prevCompareSlots);
  prevCompareSlots = slots;
  return slots;
}

// 히스토리 상태 맵의 복합 키. 세션 id 에는 개행이 올 수 없으므로 개행은 안전한 구분자다.
export function historyStateKey(sessionId: string, metricKey: string): string {
  return `${sessionId}\n${metricKey}`;
}

// 지표 토글 칩 목록 = 세션들의 metric_keys 합집합. 정렬해서 세션 순서나 도착 순서와 무관하게
// 같은 순서로 나열한다.
export function metricKeyUnion(
  metricsList: readonly (TrainingSessionMetricsView | null)[],
): string[] {
  const keys = new Set<string>();
  for (const metrics of metricsList) {
    for (const key of metrics?.metric_keys ?? []) {
      keys.add(key);
    }
  }
  return [...keys].sort();
}

// 세션 하나의 예산 단위. 상세가 아직 없으면 null 이다(축 단위 판정에서 제외).
// 비교 창은 평가 요약을 로드하지 않으므로 판정 순서는 overrides → 체크포인트 태그 → 기본이다.
export function sessionBudgetUnit(
  detail: TrainingSessionDetailView | null,
): TrainingBudgetUnit | null {
  if (detail === null) {
    return null;
  }
  return resolveSessionBudgetUnit(detail.recipe ?? null, detail.policies, []);
}

export type CompareMetricAxis = { unitLabel: "EPOCH" | "STEP"; mixed: boolean };

// 지표 하나의 공유 X축 단위. 상세를 로드한 세션들의 예산 단위로 판정한다. per-epoch 지표를
// 에폭 예산 세션과 스텝 예산 세션이 함께 그리면 같은 step 필드가 다른 뜻이 된다. 그래서 mixed 로
// 표시하고 차트가 경고 캡션을 단다. 상세를 로드하지 않은 세션은 판정에서 빠진다. 그 세션의
// 시계열은 그리지만 단위는 보증하지 않는다.
export function compareMetricAxis(
  metricKey: string,
  budgetUnits: readonly TrainingBudgetUnit[],
): CompareMetricAxis {
  const units = new Set(budgetUnits.map((unit) => metricAxisUnit(metricKey, unit)));
  if (units.size > 1) {
    return { unitLabel: "STEP", mixed: true };
  }
  return { unitLabel: units.has("epoch") ? "EPOCH" : "STEP", mixed: false };
}
